<div *transloco="let t">
	<!-- Kein pb hier: die Tabellen-Card schließt unten flush ab (Add-Zeile ist die letzte Zeile);
		 die anderen Ansichten bringen ihren Abstand selbst mit. -->
	<div class="px-4">
		@if (finishedLoading()) {
			<!-- Ansicht-Umschalter (Anlegen erfolgt über die Add-Zeilen je Sektion) -->
			<div class="w-full flex flex-row flex-wrap items-center gap-2 pb-2">
				<ara-button-toggle-group
					[value]="viewMode()"
					(valueChange)="onViewModeChange($event)"
					color="primary"
					[attr.aria-label]="t('tenantunitDetails.editor.view.switcher')">
					<button araButtonToggle value="list">{{ t('tenantunitDetails.editor.view.list') }}</button>
					<button araButtonToggle value="history">{{ t('tenantunitDetails.editor.view.history') }}</button>
					<button araButtonToggle value="timeline">{{ t('tenantunitDetails.editor.view.timeline') }}</button>
				</ara-button-toggle-group>
			</div>
		}

		<!-- Main Card with Table -->

		<!-- Panel Container -->
		<div class="w-full">
			<!-- ========================= -->
			<!-- Reusable roles group template -->
			<!-- ========================= -->

			@if (finishedLoading()) {
				@if (viewMode() === 'list') {
					<div class="pt-2 w-full">
						<!-- Vollbild-Muster: dasselbe Template rendert inline und im Vollbild-Dialog -->
						<ng-template #rolesFullscreenTpl let-isInDialog="isInDialog">
							<ara-generic-table
								#rolesTable
								[columns]="roleSectionColumns"
								[showSearch]="true"
								[searchSubject]="roleSearch$"
								[showPaginator]="false"
								[showRowActions]="true"
								[isInDialog]="isInDialog"
								[fullscreenHostTemplate]="rolesFullscreenTpl"
								[dialogTitle]="t('tenantunitDetails.persons')"
								[tableTemplate]="rolesTableTpl"></ara-generic-table>

							<ng-template #rolesTableTpl>
								<div class="table-scroll-body" [araTableScrollBody]="false">
									<table mat-table matSort (matSortChange)="onRoleSort($event)" [dataSource]="roleFlatRows()" class="w-full">
										<!-- Geteilte Gruppierungs-Marker-Spalten (Kopfzeile · „Alle anzeigen" · Add-Zeile); Zellinhalt projiziert. -->
										<ara-table-group-columns
											[columnCount]="rolesTable.displayedColumns().length"
											[headerCell]="rolesGroupHeaderTpl"
											[moreCell]="rolesGroupMoreTpl"
											[addCell]="rolesGroupAddTpl" />

										<ng-template #rolesGroupHeaderTpl let-group="group">
											<button
												type="button"
												class="ara-table-group-cell__toggle"
												(click)="toggleRoleGroup(group.key)"
												[attr.aria-expanded]="!isRoleGroupCollapsed(group.key)">
												<mat-icon fontSet="material-symbols-outlined" class="ara-table-group-cell__chevron">
													{{ isRoleGroupCollapsed(group.key) ? 'chevron_right' : 'expand_more' }}
												</mat-icon>
												<span class="ara-table-group-cell__title">{{ group.label }}</span>
												<span class="ara-count-badge">{{ group.rows.length }}</span>
											</button>
										</ng-template>

										<!-- „Alle anzeigen": vergangene Rollen sind standardmäßig zusammengefasst -->
										<ng-template #rolesGroupMoreTpl let-group="group" let-hiddenCount="hiddenCount" let-expanded="expanded">
											<div class="ara-table-group-more">
												<mat-slide-toggle [checked]="expanded" (change)="toggleRoleGroupExpanded(group.key)">
													<span class="ara-table-group-more__label">
														{{ t('tenantunitDetails.rolesView.showPast', { count: hiddenCount }) }}
													</span>
												</mat-slide-toggle>
											</div>
										</ng-template>

										<ng-template #rolesGroupAddTpl let-group="group">
											<div class="ara-table-add-row--inline">
												<ara-popover
													[content]="
														canAddRoleType(addRowRoleType(group)) ? '' : t('tenantunitDetails.editor.protectedRoleReadonly')
													">
													<button
														araButton
														color="primary"
														type="button"
														[disabled]="!canAddRoleType(addRowRoleType(group))"
														(click)="addToSection(addRowRoleType(group))">
														<mat-icon>add</mat-icon>
														<span>{{ t('tenantunitDetails.editor.addEntity', { entity: group.label }) }}</span>
													</button>
												</ara-popover>
												@if (addRowRoleType(group) === UiRoleType.RESIDENT) {
													<ara-popover [content]="hasActiveVacancy() ? t('tenantunitDetails.vacancyAddForbidden') : ''">
														<button
															araButton="stroked"
															color="secondary"
															type="button"
															[disabled]="hasActiveVacancy()"
															(click)="openPersonEditorVacancy()">
															<mat-icon>add</mat-icon>
															<span>
																{{ t('tenantunitDetails.editor.addEntity', { entity: t('tenantunitDetails.vacancy') }) }}
															</span>
														</button>
													</ara-popover>
												}
											</div>
										</ng-template>

										<ng-container matColumnDef="person">
											<th *matHeaderCellDef mat-header-cell mat-sort-header="person">{{ t('tenantunitDetails.person') }}</th>
											<td *matCellDef="let row" mat-cell>
												@if (canEditRoleModel(asRoleEntry(row).roleModel)) {
													<button
														araLink
														type="button"
														[destinationPopover]="t('tenantunits.editPersonPopover')"
														(click)="openPersonEditorEdit(asRoleEntry(row).roleModel, $event)">
														{{ asRoleEntry(row).roleModel.isVacancy ? t('enum.VACANCY') : getName(asRoleEntry(row).roleModel) }}
													</button>
												} @else {
													<ara-popover [content]="t('tenantunitDetails.editor.protectedRoleReadonly')">
														<span>
															{{ asRoleEntry(row).roleModel.isVacancy ? t('enum.VACANCY') : getName(asRoleEntry(row).roleModel) }}
														</span>
													</ara-popover>
												}
											</td>
										</ng-container>

										<ng-container matColumnDef="period">
											<th *matHeaderCellDef mat-header-cell mat-sort-header="period">{{ t('tenantunitDetails.editor.period') }}</th>
											<td *matCellDef="let row" mat-cell>
												<ara-generic-chip
													[label]="getRolePeriodLabel(asRoleEntry(row).role)"
													[tone]="isTodayRole(asRoleEntry(row).role) ? 'primary' : 'neutral'">
													<mat-icon fontSet="material-symbols-outlined" class="!text-[14px] !w-3.5 !h-3.5 shrink-0">
														{{ roleStatusIcon(asRoleEntry(row).role) }}
													</mat-icon>
												</ara-generic-chip>
											</td>
										</ng-container>

										<ng-container matColumnDef="permissions">
											<th *matHeaderCellDef mat-header-cell>{{ t('permissions.permissions') }}</th>
											<td *matCellDef="let row" mat-cell>
												@if (canHavePermissionCode(asRoleEntry(row).role)) {
													<ara-permission-widget
														[permissions]="permissions?.[asRoleEntry(row).role.id] ?? []"
														[dialogParameters]="setPermissionDialogData(asRoleEntry(row).role, asRoleEntry(row).roleModel)"
														[canOpenDialog]="canOpenPermissionsDialog(asRoleEntry(row).role)"
														[canCreatePermission]="canCreatePermission(asRoleEntry(row).role)"
														[canModifyPermissions]="canManagePermissionsForRole(asRoleEntry(row).role)"
														[permissionsDisabledReason]="permissionsDisabledReason(asRoleEntry(row).role)"
														(permissionsChanged)="notifyRoleChange()"></ara-permission-widget>
												} @else {
													<span class="text-black/25">—</span>
												}
											</td>
										</ng-container>

										<ng-container matColumnDef="uvi">
											<th *matHeaderCellDef mat-header-cell>{{ t('tenantunitDetails.uviOptions') }}</th>
											<td *matCellDef="let row" mat-cell>
												@if (!asRoleEntry(row).roleModel.isVacancy && asRoleEntry(row).role.roleType === UiRoleType.RESIDENT) {
													<ara-popover [content]="isWorkOrderIssued() ? '' : t('tenantunitDetails.uviNotActivatedTooltip')">
														<!-- Icon + Label; flex zentriert den Inhalt vertikal in der Zeile (ein reines
														 inline mat-icon sitzt auf der Textbasislinie und wirkt nach oben verschoben). -->
														<span class="flex items-center gap-2">
															<mat-icon
																fontSet="material-symbols-outlined"
																class="text-gray-600 shrink-0"
																[matBadge]="isWorkOrderIssued() ? null : 'X'"
																matBadgeColor="warn">
																{{ uviIcon(asRoleEntry(row).role) }}
															</mat-icon>
															<span>{{ t(uviLabelKey(asRoleEntry(row).role)) }}</span>
														</span>
													</ara-popover>
												} @else {
													<span class="text-black/25">—</span>
												}
											</td>
										</ng-container>

										<ng-container matColumnDef="hv">
											<th *matHeaderCellDef mat-header-cell>{{ t('tenantunitDetails.tenantNumber') }}</th>
											<td *matCellDef="let row" mat-cell>
												@if (asRoleEntry(row).role.roleType === UiRoleType.RESIDENT && asRoleEntry(row).role.propertyManagementNumber) {
													<ara-generic-chip [label]="asRoleEntry(row).role.propertyManagementNumber"></ara-generic-chip>
												} @else {
													<span class="text-black/25">—</span>
												}
											</td>
										</ng-container>

										<ng-container matColumnDef="actions">
											<th *matHeaderCellDef mat-header-cell></th>
											<td *matCellDef="let row" mat-cell>
												<div class="flex items-center justify-end gap-1">
													<ara-popover [content]="t(uviJumpTooltipKey(asRoleEntry(row).role))">
														<button
															araButton="icon"
															color="secondary"
															class="uvi-jump-button"
															(click)="routeToUvi()"
															[disabled]="!canJumpToUvi(asRoleEntry(row).role)"
															[attr.aria-label]="t('uvi.jumpToUvi')">
															<mat-icon fontSet="material-symbols-outlined">speed</mat-icon>
														</button>
													</ara-popover>
													<ara-popover [content]="t(personEditTooltipKey(asRoleEntry(row).roleModel))">
														<button
															araButton="icon"
															color="secondary"
															[disabled]="!canEditRoleModel(asRoleEntry(row).roleModel)"
															(click)="openPersonEditorEdit(asRoleEntry(row).roleModel, $event)"
															[attr.aria-label]="t('tenantunitDetails.personEdit')">
															<mat-icon fontSet="material-symbols-outlined">edit</mat-icon>
														</button>
													</ara-popover>
													<ara-popover [content]="t(deleteRoleTooltipKey(asRoleEntry(row).role))">
														<button
															araButton="icon"
															color="error"
															(click)="deleteRole(asRoleEntry(row).role)"
															[disabled]="!canDeleteRole(asRoleEntry(row).role)"
															[attr.aria-label]="t('tenantunitDetails.roleDelete')">
															<mat-icon fontSet="material-symbols-outlined">delete</mat-icon>
														</button>
													</ara-popover>
												</div>
											</td>
										</ng-container>

										<tr *matHeaderRowDef="rolesTable.displayedColumns()" mat-header-row></tr>
										<tr
											*matRowDef="let row; columns: groupRowDefs.header.columns; when: groupRowDefs.header.when"
											mat-row
											class="ara-table-group-row"></tr>
										<tr
											*matRowDef="let row; columns: groupRowDefs.more.columns; when: groupRowDefs.more.when"
											mat-row
											class="ara-table-group-more-row"></tr>
										<tr
											*matRowDef="let row; columns: groupRowDefs.add.columns; when: groupRowDefs.add.when"
											mat-row
											class="ara-table-group-add-row"></tr>
										<tr *matRowDef="let row; columns: rolesTable.displayedColumns()" @fadeInUp mat-row></tr>
									</table>
								</div>
							</ng-template>
						</ng-template>

						<ng-container *ngTemplateOutlet="rolesFullscreenTpl; context: { isInDialog: false }"></ng-container>
					</div>
				} @else if (hasAnyRole()) {
					<div class="pb-4">
						@if (viewMode() === 'history') {
							<ara-roles-history [roleModels]="sortedRoleModels()" (openPerson)="openPersonEditorEdit($event)"></ara-roles-history>
						} @else {
							<ara-roles-branch-timeline
								[roleModels]="sortedRoleModels()"
								(openPerson)="openPersonEditorEdit($event)"></ara-roles-branch-timeline>
						}
					</div>
				} @else {
					<div @fadeInUp class="flex flex-col items-center justify-center text-center py-14 px-4">
						<mat-icon class="text-gray-400 text-4xl w-9 h-9 mb-2.5">info</mat-icon>
						<div class="text-gray-500 text-base">{{ t('common.noData') }}</div>
					</div>
				}
			} @else {
				<div @fadeInUp class="flex flex-row justify-center items-center py-12">
					<mat-spinner color="primary" diameter="36" mode="indeterminate"></mat-spinner>
				</div>
			}
		</div>
	</div>
</div>
